import { storeToRefs } from "pinia";
import { type Ref, ref } from "vue";

import { ErrorDialog } from "shared/boot/alert";
import { $streemApiV1 } from "shared/boot/api";
import { getLocaleText } from "shared/boot/i18n";
import DateRange from "shared/helpers/DateRange";
import transformKeys from "shared/helpers/transformKeys";
import { type AppConfig } from "shared/services/appConfig/types";
import useAIStore, { type AISummaryResult } from "shared/stores/ai";
import useGlobalStore from "shared/stores/global";
import { type Nullable } from "shared/types";

export type AIChatHistoryRole = "user" | "model";
export type AIChatHistorySource =
  | "boolean_creator"
  | "general_assistant"
  | "stream_summary";

interface AIChatHistoryBaseResponse {
  created_at: string;
  group_id: number;
  id: number;
  message: string;
  organisation_id: number;
  role: AIChatHistoryRole;
  source: AIChatHistorySource;
  updated_at: string;
  user_id: number;
}

export interface AIChatHistoryResponse extends AIChatHistoryBaseResponse {
  message: string;
}

export interface AIChatHistoryMessage {
  booleanQuery?: string;
  prompt?: string;
  text?: string;
}

export interface AIChatHistory
  extends AIChatHistoryBaseResponse,
    AIChatHistoryMessage {}

interface AIChatSummary extends AISummaryResult {
  after: number;
  before: number;
}

function parseMessage(message: string): AIChatHistoryMessage {
  try {
    return JSON.parse(message);
  } catch {
    return {
      text: getLocaleText(
        "ai_boolean_creator_chat_history.fail_to_parse_chat_history"
      ),
    };
  }
}

function parseSummaryMessage(message: string): AIChatSummary {
  try {
    return transformKeys(JSON.parse(message));
  } catch {
    return {
      after: 0,
      aiFeatureConfigurationId: 0,
      archivedAt: "",
      before: 0,
      createdAt: "",
      id: 0,
      organisationId: 0,
      streamId: 0,
      topics: [],
    };
  }
}

function mapChatHistory(data: AIChatHistoryResponse): AIChatHistory {
  const message: AIChatHistoryMessage =
    data.role === "model" && data.source === "general_assistant"
      ? {
          text: data.message,
        }
      : parseMessage(data.message);

  return {
    ...data,
    ...message,
  };
}

function getStartingMessages(
  message: AIChatHistory,
  platformName?: string
): AIChatHistory[] {
  if (message.source === "boolean_creator") {
    return [
      {
        ...message,
        text: "",
        role: "user",
        prompt: getLocaleText(
          "boolean_creator_chat.boolean_mention_stream_creator"
        ),
      },
      {
        ...message,
        prompt: "",
        role: "model",
        text: getLocaleText("boolean_creator_chat.pre_suggestion_text"),
      },
    ];
  }

  if (message.source === "general_assistant") {
    return [
      {
        ...message,
        prompt: "",
        role: "model",
        text: getLocaleText("ai_assistant_panel.help_message", {
          platformName,
        }),
      },
    ];
  }

  if (message.source === "stream_summary") {
    return [
      {
        ...message,
        prompt: "",
        role: "model",
        text: getLocaleText("ai_summary_panel.title"),
      },
    ];
  }

  return [];
}

export function useAIChatHistory() {
  const { appConfig } = storeToRefs(useGlobalStore()) as unknown as {
    appConfig: Ref<AppConfig>;
  };

  const aiStore = useAIStore();
  const { aiSummaryChatHistory } = storeToRefs(aiStore);

  const messages = ref<AIChatHistory[]>([]);
  const sourceType = ref<Nullable<AIChatHistorySource>>(null);
  const title = ref<string>("");

  function handleSummaryHistory(
    firstMessage: AIChatHistory,
    startingMessages: AIChatHistory[]
  ) {
    messages.value = startingMessages;

    const { after, before, ...result } = parseSummaryMessage(
      firstMessage.message
    );

    const dateRange = new DateRange({ after, before });

    aiSummaryChatHistory.value = {
      dateRange,
      dateRangeUpdated: false,
      error: false,
      feedbackId: 0,
      loading: false,
      result,
      status: null,
      thumbsUp: null,
    };
  }

  async function getAIHistory(groupId: Nullable<number>) {
    try {
      const { data } = await $streemApiV1.get<AIChatHistoryResponse[]>(
        `ai/chat_histories/group/${groupId}`
      );

      const historyMessages = data.map(mapChatHistory);
      const [firstMessage] = historyMessages;
      sourceType.value = firstMessage.source;

      const startingMessages = getStartingMessages(
        firstMessage,
        appConfig.value.platformName
      );

      title.value = firstMessage.prompt || firstMessage.text || "";

      if (sourceType.value === "stream_summary") {
        handleSummaryHistory(firstMessage, startingMessages);
      } else {
        messages.value = startingMessages.concat(historyMessages);
      }
    } catch (error) {
      ErrorDialog(
        getLocaleText(
          "ai_boolean_creator_chat_history.fail_to_load_chat_history"
        ),
        error
      );
    }
  }

  return {
    messages,
    sourceType,
    title,

    getAIHistory,
  };
}
